// src/services/offboardingService.js
// FuMind_GM HR - Exit / Offboarding Service

const EXIT_LOAD_FLOW_URL = "/tracka-flow/v1/EXIT_LOAD";
const EXIT_ACTION_FLOW_URL = "/tracka-flow/v1/EXIT_ACTION";
const EXIT_API_KEY = "";

export const EXIT_TYPES = ["Employee Resignation", "Company Termination", "Layoff / Position Redundancy", "Mutual Separation", "Retirement", "Other"];
export const EXIT_REASONS = ["Career Growth", "Compensation", "Relocation", "Personal Reasons", "Higher Studies", "Health Reasons", "Company Termination", "Layoff / Redundancy", "Retirement", "Mutual Separation", "Other"];
export const CLEARANCE_STATUSES = ["Pending", "In Progress", "Approved", "Rejected", "Waived"];
export const SETTLEMENT_STATUSES = ["Not Started", "In Progress", "Completed", "On Hold", "Not Applicable"];

export function text(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return text(value.Value ?? value.value ?? value.Title ?? value.DisplayName ?? value.Email ?? "", fallback);
  return String(value).trim() || fallback;
}

function safeJsonParse(value, fallback = null) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  const parsed = safeJsonParse(value, []);
  return Array.isArray(parsed) ? parsed : [];
}

export function normalizeRole(role) {
  const value = String(role || "").toLowerCase().trim();
  if (value.includes("hradmin") || value === "hr admin") return "HRAdmin";
  if (value === "admin" || value.includes("administrator")) return "Admin";
  if (value === "hr") return "HR";
  if (value.includes("manager")) return "Manager";
  return "Employee";
}

export function normalizeUser(user = {}) {
  const employeeId = user.employeeId || user.employeeCode || user.EmployeeID || user.EmployeeCode || "";
  const email = user.email || user.workEmail || user.Email || user.WorkEmail || "";
  return {
    employeeId,
    employeeCode: user.employeeCode || employeeId,
    workEmail: user.workEmail || email,
    email,
    displayName: user.displayName || user.name || user.employeeName || user.EmployeeName || employeeId || email || "Employee",
    role: normalizeRole(user.role || user.Role || "Employee"),
  };
}

function readableError(value, fallback = "Exit flow returned failure.") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.message || value.error || value.detail || JSON.stringify(value);
  return String(value);
}

async function postToFlow(url, body) {
  if (!url || url.includes("PASTE_")) throw new Error("Exit / Offboarding flow URL is not configured.");
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({  ...body }),
  });
  const raw = await response.text();
  const data = safeJsonParse(raw, { raw });
  if (!response.ok) throw new Error(`Exit flow failed (${response.status}): ${readableError(data)}`);
  if (data?.success === false) throw new Error(readableError(data));
  return data;
}

export function normalizeExitRequest(row = {}) {
  return {
    id: text(row.ID || row.id),
    exitRequestId: text(row.ExitRequestId || row.exitRequestId || row.Title),
    employeeId: text(row.EmployeeId || row.employeeId || row.EmployeeID),
    workEmail: text(row.WorkEmail || row.workEmail),
    employeeName: text(row.EmployeeName || row.employeeName),
    exitType: text(row.ExitType || row.exitType, "Employee Resignation"),
    requestedDate: text(row.RequestedDate || row.requestedDate),
    noticeStartDate: text(row.NoticeStartDate || row.noticeStartDate),
    requestedLastWorkingDate: text(row.RequestedLastWorkingDate || row.requestedLastWorkingDate),
    approvedLastWorkingDate: text(row.ApprovedLastWorkingDate || row.approvedLastWorkingDate),
    noticePeriodDays: text(row.NoticePeriodDays || row.noticePeriodDays),
    exitReason: text(row.ExitReason || row.exitReason),
    detailedReason: text(row.DetailedReason || row.detailedReason),
    managerReviewStatus: text(row.ManagerReviewStatus || row.managerReviewStatus, "Pending"),
    managerRemarks: text(row.ManagerRemarks || row.managerRemarks),
    hrReviewStatus: text(row.HRReviewStatus || row.hrReviewStatus, "Pending"),
    hrRemarks: text(row.HRRemarks || row.hrRemarks),
    exitStatus: text(row.ExitStatus || row.exitStatus, "Submitted"),
    exitInterviewStatus: text(row.ExitInterviewStatus || row.exitInterviewStatus, "Not Started"),
    settlementStatus: text(row.SettlementStatus || row.settlementStatus, "Not Started"),
    settlementRemarks: text(row.SettlementRemarks || row.settlementRemarks),
    relievingDate: text(row.RelievingDate || row.relievingDate),
    isCompanyInitiated: row.IsCompanyInitiated === true || row.isCompanyInitiated === true || text(row.IsCompanyInitiated).toLowerCase() === "yes",
    isActive: row.IsActive !== false && row.isActive !== false,
    raw: row,
  };
}

export function normalizeClearance(row = {}) {
  return {
    id: text(row.ID || row.id),
    clearanceId: text(row.ClearanceId || row.clearanceId || row.Title),
    exitRequestId: text(row.ExitRequestId || row.exitRequestId),
    employeeId: text(row.EmployeeId || row.employeeId),
    workEmail: text(row.WorkEmail || row.workEmail),
    employeeName: text(row.EmployeeName || row.employeeName),
    departmentName: text(row.DepartmentName || row.departmentName, "HR"),
    assignedToEmployeeId: text(row.AssignedToEmployeeId || row.assignedToEmployeeId),
    assignedToEmail: text(row.AssignedToEmail || row.assignedToEmail),
    clearanceStatus: text(row.ClearanceStatus || row.clearanceStatus, "Pending"),
    clearanceRemarks: text(row.ClearanceRemarks || row.clearanceRemarks),
    clearanceDate: text(row.ClearanceDate || row.clearanceDate),
    clearedBy: text(row.ClearedBy || row.clearedBy),
    raw: row,
  };
}

function normalizeResponse(data = {}) {
  return {
    exitRequests: asArray(data.exitRequests || data.requests || data.exits).map(normalizeExitRequest),
    clearances: asArray(data.clearances || data.exitClearances).map(normalizeClearance),
    raw: data,
  };
}

export async function loadExitData({ currentUser, payload = {} }) {
  const data = await postToFlow(EXIT_LOAD_FLOW_URL, { action: "loadExitData", currentUser: normalizeUser(currentUser), payload });
  return normalizeResponse(data);
}

async function actionFlow(action, { currentUser, payload = {} }) {
  return postToFlow(EXIT_ACTION_FLOW_URL, { action, currentUser: normalizeUser(currentUser), payload });
}

export const createExitRequest = (args) => actionFlow("createExitRequest", args);
export const managerReviewExit = (args) => actionFlow("managerReview", args);
export const hrReviewExit = (args) => actionFlow("hrReview", args);
export const saveClearance = (args) => actionFlow("saveClearance", args);
export const saveExitInterview = (args) => actionFlow("saveExitInterview", args);
export const saveSettlement = (args) => actionFlow("saveSettlement", args);
export const closeExit = (args) => actionFlow("closeExit", args);
export const cancelExit = (args) => actionFlow("cancelExit", args);
export const reopenExit = (args) => actionFlow("reopenExit", args);
